import { Api, HorizontalAlignment, ModelRow, PermissionsUtil } from "@mcleod/core";
import {
    Button, ChangeEvent, ClickEvent, DataSourceMode, Label, LookupModelSelectionEvent, Panel, Snackbar, Step, TableRow, Textbox, Toast
} from "@mcleod/components";
import { CommonDialogs } from "@mcleod/common";
import { getDispatchControlBoolean, getDispatchControlValue, LMEGeneralSettings } from "@mcleod/general";
import { ModelCustomer } from "@mcleod/ar/";
import { AutogenLayoutPreassignmentStepper } from "./autogen/AutogenLayoutPreassignmentStepper";
import {
    EquipmentItem, EquipmentGroupValidation, EquipmentGroupValidationProps, EquipmentGroupValidationResult
 } from "./EquipmentGroupValidation";
import { ModelLocation } from "../models/ModelLocation";
import { DriverMessageType } from "../DriverSendMessage";
import { SendLoad } from "./SendLoad";
import { UnassignDialog} from "../UnassignDialog";
import { UnassignEquipmentDialog } from "../UnassignEquipmentDialog";
import { MultiPreassignment } from "./MultiPreassignment";
import { TrailerUtil } from "../TrailerUtil";

const DRIVER_ONE = "driver_one";
const DRIVER_TWO = "driver_two";

export class PreassignmentStepper extends AutogenLayoutPreassignmentStepper {
    public actionButton: Button;
    public continueButton: Button;
    public orderId: string;
    public manifestId: string;
    public movementId: string;
    public preassigned: boolean;
    public unassigning: boolean;
    public showAssignToast:boolean = false;
    public isMultiSelect: boolean = false;

    get mp(): MultiPreassignment {
        return this.layoutMultiPreassignment;
    }

    override onLoad() {
        this.setupSelectionListeners();
        this.addBeforeLookupModelListeners();
        TrailerUtil.setupMaxTrailers(this.textboxTrailer, this.textboxTrailer2, this.textboxTrailer3);
    }

    addBeforeLookupModelListeners() {
        this.textboxDriverOne.addBeforeLookupModelSearchListener((event) => event.filter.is_active = 'Y');
        this.textboxDriverTwo.addBeforeLookupModelSearchListener((event) => event.filter.is_active = 'Y');
        this.textboxTractor.addBeforeLookupModelSearchListener((event) => event.filter.service_status = 'A');
        this.textboxTrailer.addBeforeLookupModelSearchListener((event) => event.filter.is_active = 'A');
        this.textboxTrailer2.addBeforeLookupModelSearchListener((event) => event.filter.is_active = 'A');
        this.textboxTrailer3.addBeforeLookupModelSearchListener((event) => event.filter.is_active = 'A');
    }

    setupActionButton(actionButton: Button) {
        this.actionButton = actionButton;
        this.actionButton.dataSource = this.mainDataSource;
        this.actionButton.addClickListener((event: ClickEvent) => this.doAction());
    }

    setupContinueButton(continueButton: Button) {
        this.continueButton = continueButton;
        this.continueButton.addClickListener((event: ClickEvent) => this.slideOut());
    }

    /** This is an event handler for the beforeStepListener event of stepperPreassignment. */
    async stepperPreassignmentBeforeStepListener(event) {
        if (event.oldIndex > event.newIndex || (!this.actionButton.enabled && !this.actionButton.busy)) {
            event.preventDefault();
            return;
        }

        switch (event.oldStep) {
            case this.stepSelect:
                if (!this.isMultiSelect && !this.preassigned && event.newStep === this.stepAssignments
                        && this.mainDataSource.mode == DataSourceMode.ADD) {
                    event.preventDefault();
                    await this.preassign();
                }
                break;
        }

        switch (event.newStep) {
            case this.stepZmitLoad:
                if (!this.preassigned) {
                    const equipmentRow = new ModelRow(this.sourceEquipmentGroup.url, false, {});
                    equipmentRow.setValuesAndLookupModelData(this.sourcePreassignments.activeRow);
                    this.sourceEquipmentGroup.setRowsAndMode(DataSourceMode.NONE, [equipmentRow]);
                }
                this.setShipperConsigneeValues();
                const response = await SendLoad.validateUnits(this.activeRow.get("tractor_id"), this.activeRow.get("driver_id"), this.activeRow.get("driver_two_id"));
                if (response.data) {
                    this.layoutSendLoad.displayValidMcUnits(response.data[0]);
                }
                break;
        }
        this.continueButton.visible = event.newStep === this.stepZmitLoad;
        this.updateActionButton(event.newStep);
    }

    updateActionButton(step: Step) {
        let caption: string;
        if (step === this.stepSelect) {
            caption = "Preassign & Continue";
        } else if (step === this.stepAssignments) {
            caption = "Save & Continue";
        } else if (step === this.stepZmitLoad) {
            caption ="Send Now";
        }
        this.actionButton.caption = caption;
    }

    async doAction() {
        switch (this.stepperPreassignment.getActiveStep()) {
            case this.stepSelect:
                this.preassign();
                break;
            case this.stepAssignments:
                this.reSequence();
                break;
            case this.stepZmitLoad:
                const sendEmail: boolean = this.switchSendEmail.checked;
                const validEmail = sendEmail && !this.layoutDriverSendMessage.activeRow.isNull("email") && this.layoutDriverSendMessage.validateEmailString(this.layoutDriverSendMessage.activeRow.get("email"));
                if (sendEmail && !validEmail) {
                    const yesNo = await CommonDialogs.showYesNo(this.createEmailWarningPanel(), "Load Warning");
                    if (yesNo == false || yesNo == undefined) {
                        return;
                    }
                }
                this.layoutSendLoad.send().then(success => {
                    if (success) {
                        if (sendEmail && validEmail) {
                            this.layoutDriverSendMessage.performSendMessage(true);
                        }
                        this.slideOut().then(() => Toast.showSuccessToast("Load Sent Successfully"));
                    }
                });
                break;
        }
    }

    getSortedSelectedRows(): TableRow[] {
        return this.mp.table.selectedRows.sort((a, b) => {
            return a.index - b.index;
        });
    }

    async preassign() {
        if (!this.sourcePreassignments.validate())
            return;
        this.actionButton.busy = true;
        try {
            if (this.isMultiSelect) {
                if (this.mp.table.selectedRows.length < 1) {
                    CommonDialogs.showError("Please select at least one row to preassign.");
                    return;
                }

                const moveId = this.mp.linkedId == null ? this.mp.table.selectedRows[0].data.get("movement_id") : this.getSortedSelectedRows()[0].data.get("movement_id");
                this.movementId = this.layoutSendLoad.movementId = moveId;
                if (!(await this.unassignMultiple())) {
                    return;
                }
                if (!(this.mp.linkedId != null ? await this.preassignSingle() : await this.preassignMulti())) {
                    CommonDialogs.showError("One or more assignments could not be processed due to an error and have been automatically deselected.", "Server Error");
                    return;
                }
            } else {
                if (this.unassigning) {
                    if (!(await this.unassign()))
                        return;
                }
                if (!this.passesTrailerCheck()) {
                    CommonDialogs.showError("The same trailer cannot be assigned more than once.");
                    return;
                }
                if (!(await this.preassignSingle())) {
                    return;
                }
            }
            this.stepperPreassignment.selectedIndex = this.stepAssignments.index;
            this.showAssignToast = true;
            this.layoutEquipmentAssignments.isPreassigning = true;
            this.layoutEquipmentAssignments.textboxEquipment.visible = true;
            this.layoutEquipmentAssignments.setup(this.movementId, this.getEquipmentId(), this.activeRow.get("tractor_id"));
            this.setupDriverSendMessage();
        } finally {
            this.actionButton.busy = false;
        }
    }

    async preassignSingle(): Promise<boolean> {
        if (this.trailerIdChanged()) {
            await Api.post("lme/dispatch/asset/trailer-assignment", {
                movement_id: this.movementId,
                trailer_id: this.activeRow.get("trailer_id", null),
                trailer2_id: this.activeRow.get("trailer2_id", null),
                trailer3_id: this.activeRow.get("trailer3_id", null)
            });
        }
        this.activeRow.set("movement_id", this.movementId);
        await this.mainDataSource.post();
        return true;
    }

    async preassignMulti(): Promise<boolean> {
        const res = await Api.post("lme/dispatch/asset/preassignments/batch", {
            keys: this.mp.table.selectedRows.map(row => ({ movement_id: row.data.get("movement_id") })),
            data: { tractor_id: this.mp.tractorId, driver_id: this.mp.driverId, driver_two_id: this.mp.driver2Id ?? null }
        });
        if (res.data.length > 0) {
            this.mp.handleServerErrors(res.data);
        }
        return res.data.length == 0;
    }

    async unassign(): Promise<boolean> {
        const reqReason = getDispatchControlBoolean("req_unassign_reason", false);
        const resultRow = reqReason ? await new UnassignDialog("B").show() : null;
        if (resultRow || !reqReason) {
            try {
                await Api.post("lme/dispatch/asset/unassign-equipment", {
                    movement_id: this.movementId,
                    unassign_reason: resultRow instanceof ModelRow ? resultRow : null
                });
                return true;
            } catch (e) {
                Snackbar.showWarningSnackbar(e.messages[0]);
                return false;
            }
        } else {
            return false;
        }
    }

    async unassignMultiple(): Promise<boolean> {
        const assignedRows = this.mp.table.selectedRows.filter(row => {
            return getDispatchControlValue("dispatch_by") == "T"
                ? !row.data.isNull("tractor") && row.data.get("tractor") != this.mp.tractorId
                : !row.data.isNull("driver1_id") && row.data.get("driver1_id") != this.mp.driverId;
        })
        if (assignedRows.length > 0) {
            if (!(await this.batchUnassign(assignedRows))) {
                return false;
            }
        }
        return true;
    }

    async batchUnassign(assignedRows: TableRow[]): Promise<boolean> {
        const reqReason = getDispatchControlBoolean("req_unassign_reason", false);
        const unassignReason = reqReason ?
            await new UnassignEquipmentDialog({ codeType: "B", isMultiSelect: true }).show() : null;
        if (unassignReason || !reqReason) {
            await Api.post("lme/dispatch/asset/unassign-equipment/batch", {
                "keys": assignedRows.map(row => ({ movement_id: row.data.get("movement_id") })),
                "data": {
                    "unassign_reason": unassignReason instanceof ModelRow ? unassignReason : null
                }
            });
            return true;
        } else {
            return false;
        }
    }

    async reSequence() {
        this.layoutEquipmentAssignments.saveButton = this.actionButton;
        await this.layoutEquipmentAssignments.saveResequence();
        if (this.layoutEquipmentAssignments.unassignedOpenedMove)
            this.showAssignToast = false;
        this.stepperPreassignment.selectedIndex = this.stepZmitLoad.index;
    }

    async searchEquipmentGroup(equipmentGroupId: string) {
        await this.sourceEquipmentGroup.search({ id: equipmentGroupId });
        if (this.sourceEquipmentGroup.data.length > 0) {
            await this.validateEquipmentGroup({
                movementId: this.movementId,
                orderId: this.orderId,
                modelRow: this.sourceEquipmentGroup.activeRow,
                field: EquipmentGroupValidation.getDispatchByField(),
                driverId: this.sourceEquipmentGroup.activeRow.get("driver_id"),
                driver2Id: this.sourceEquipmentGroup.data.length > 1 ? this.sourceEquipmentGroup.data[1].get("driver_id") : null,
                tractorId: this.sourceEquipmentGroup.activeRow.get("tractor_id"),
                equipmentGroupWarnings: this.layoutEquipmentGroupWarnings,
                preassignment: true
            });
            const egRow = this.sourceEquipmentGroup.activeRow;
            Object.entries(egRow.data).forEach(([key, value]) => {
                const lmData = egRow.getLookupModelData(key);
                if (lmData) {
                    this.activeRow?.setLookupModelData(key, lmData);
                }
                this.activeRow?.set(key, value);
            });
            if (this.sourceEquipmentGroup.data.length > 1) {
                const driverTwoRow = this.sourceEquipmentGroup.data[1];
                const driverTwoId = driverTwoRow?.get("driver_id", null)
                const driverTwoIdLmData = driverTwoRow?.getLookupModelData("driver_id");
                this.activeRow?.setValueAndLookupModelData("driver_two_id", driverTwoId, driverTwoIdLmData);
            }
            const trailer = this.activeRow?.get("trailer_id")
            if (trailer != null) {
                this.activeRow.setLookupModelData("trailer_id", new ModelRow(this.textboxTrailer.lookupModel, false,
                    {"id": trailer}));
            }
            this.mainDataSource.redisplayRow(0, this.activeRow ?? null, null);
            this.setupDriverSendMessage();
        }
    }

    handleEquipmentGroupValidationResult(validationResult: EquipmentGroupValidationResult) {
        this.setProhibitedDriverValues(validationResult.driverProhibitedValues);
        if (validationResult.driverEvents?.length > 0) {
            EquipmentGroupValidation.showPriorityEventSnackbar(validationResult.driverEvents, EquipmentGroupValidation.driverEventPreassignFilter);
        }
    }

    trailerIdChanged() {
        return this.activeRow.getChangedData().hasOwnProperty("trailer_id") ||
                this.activeRow.getChangedData().hasOwnProperty("trailer2_id") ||
                    this.activeRow.getChangedData().hasOwnProperty("trailer3_id");
    }

    getEquipmentId(): string {
        if (getDispatchControlValue("dispatch_by") === EquipmentItem.TRACTOR) {
            return this.activeRow.get("tractor_id");
        } else {
            return this.activeRow.get("driver_id");
        }
    }

    setupSelectionListeners() {
        this.textboxDriverOne.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => this.onEquipmentSelect(event));
        this.textboxDriverTwo.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => this.onEquipmentSelect(event));
        this.textboxTractor.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => this.onEquipmentSelect(event));
        this.textboxTrailer.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => this.onEquipmentSelect(event));
        this.textboxTrailer2.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => this.onEquipmentSelect(event));
        this.textboxTrailer3.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => this.onEquipmentSelect(event));
    }

    async onEquipmentSelect(event: LookupModelSelectionEvent) {
        const textbox = event.target as Textbox;
        const baseEquipment = {
            tractorId: this.activeRow.get("tractor_id", null),
            driverId: this.activeRow.get("driver_id", null),
            driver2Id: this.activeRow.get("driver_two_id", null),
            trailerId: this.activeRow.get("trailer_id", null),
        }
        if (event.hasDeselection() && !event.hasSelection()) {
            if (this.isMultiSelect) {
                this.validatAssignmentsForMultiSelect();
                return;
            } else {
                if (!textbox.field.includes("trailer")) { // we need to run validation for trailers since there can be multiple
                    this.layoutEquipmentGroupWarnings.refresh(Object.values(baseEquipment));
                    if (!event.hasDeselection() && !event.hasSelection())
                        return;
                }
            }
        }
        this.actionButton.enabled = false;
        this.actionButton.disabledTooltip = "Preassign is disabled while validation is in progress";
        this.updateEquipmentTextboxInteractions(false);
        // this.setTooltipContent(textbox);
        const modelRow = textbox.getRelevantModelRow();
        await this.validateEquipmentGroup({
            modelRow, field: textbox.field,
            movementId: this.movementId,
            orderId: this.orderId,
            ...baseEquipment,
            trailer2Id: this.activeRow.get("trailer2_id", null),
            trailer3Id: this.activeRow.get("trailer3_id", null),
            equipmentGroupWarnings: this.layoutEquipmentGroupWarnings,
            preassignment: true
        });

        if (this.isMultiSelect) {
            this.validatAssignmentsForMultiSelect();
        }
    }

    public async validateEquipmentGroup(props: EquipmentGroupValidationProps) {
        const validationResult = await EquipmentGroupValidation.validate(props);
        this.handleEquipmentGroupValidationResult(validationResult);
        this.actionButton.enabled = this.preassigned || !EquipmentGroupValidation.hasErrors(validationResult);
        this.actionButton.disabledTooltip = "Preassign is disabled due to invalid equipment.";
        this.updateEquipmentTextboxInteractions(true);
    }

    private updateEquipmentTextboxInteractions(enabled: boolean) {
        this.panelEquipmentTextboxes.forEveryChildComponent(comp => comp._interactionEnabled = enabled);
    }

    private validatAssignmentsForMultiSelect() {
        this.mp.tractorId = this.activeRow.get("tractor_id", null);
        this.mp.driverId = this.activeRow.get("driver_id", null);
        this.mp.driver2Id = this.activeRow.get("driver_two_id", null);
        this.mp.validateAssignments();
        this.updateEquipmentTextboxInteractions(true);
    }

    private passesTrailerCheck(): boolean {
        const maxTrailers = LMEGeneralSettings.getSingleton().getDispatchControl().maximum_trailers;
        if (maxTrailers && maxTrailers == 2) {
            return this.activeRow.get("trailer_id", -1) != this.activeRow.get("trailer2_id", -2);
        } else if (maxTrailers == 3) {
            return new Set([this.activeRow.get("trailer_id", -1), this.activeRow.get("trailer2_id", -2), this.activeRow.get("trailer3_id", -3)]).size === 3;
        }
        return true;
    }

    private async setProhibitedDriverValues(driverProhibitedValues) {
        const driver1Prohibited = driverProhibitedValues.driver1Location != null || driverProhibitedValues.driver1Customer != null;
        const driver2Prohibited = driverProhibitedValues.driver2Location != null || driverProhibitedValues.driver2Customer != null;
        if (driver1Prohibited) {
            this.mainDataSource.activeRow?.set("prohibited_driver_one", driverProhibitedValues.driver1Name);
            const driver1Location: string = driverProhibitedValues.driver1Location;
            const driver1Customer: string = driverProhibitedValues.driver1Customer;
            await this.setProhibitedInformation(driver1Location, driver1Customer, DRIVER_ONE);
        }

        if (driver2Prohibited) {
            this.mainDataSource.activeRow?.set("prohibited_driver_two", driverProhibitedValues.driver2Name);
            const driver2Location: string = driverProhibitedValues.driver2Location;
            const driver2Customer: string = driverProhibitedValues.driver2Customer;
            await this.setProhibitedInformation(driver2Location, driver2Customer, DRIVER_TWO);
        }
    }

    private async setProhibitedInformation(location: string, customer: string, sequencePrefix: string) {
        if (location) {
            await this.setProhibitedLocationLookupData(location, sequencePrefix);
        }

        if (customer) {
            await this.setProhibitedCustomerLookupData(customer, sequencePrefix);
        }
    }

    private async setProhibitedLocationLookupData(location: string, sequencePrefix: string) {
        await new ModelLocation().searchSingle({ id: location }, "lme/dispatch/Location").then(rowLocation => {
            if (rowLocation == null) {
                return;
            }
            this.mainDataSource.activeRow.setLookupModelData(sequencePrefix + "_prohibited_location", rowLocation.data);
            this.mainDataSource.activeRow?.set(sequencePrefix + "_prohibited_location", rowLocation.data["id"]);
        });
    }

    private async setProhibitedCustomerLookupData(customer: string, sequencePrefix: string) {
        await new ModelCustomer().searchSingle({ id: customer }).then(rowCustomer => {
            if (rowCustomer == null) {
                return;
            }
            this.mainDataSource.activeRow.setLookupModelData(sequencePrefix + "_prohibited_customer", rowCustomer.data);
            this.mainDataSource.activeRow?.set(sequencePrefix + "_prohibited_customer", rowCustomer.data["id"]);
        });
    }

    async setupZmit() {
        this.layoutSendLoad.sendButton = this.actionButton;
        this.layoutSendLoad.movementId = this.movementId;
        if (!this.sourceZmitLoad.activeRow?.getBoolean("mobile_comm_active", false))
            this.stepperPreassignment.removeStep(this.stepZmitLoad);
        this.continueButton.caption = "Send Later";
    }

    switchSendEmailOnChange(event: ChangeEvent) {
        this.layoutDriverSendMessage.visible = event.newValue;
        localStorage.setItem("send_driver_email", JSON.stringify(event.newValue));
    }

    async setupDriverSendMessage() {
        const canSendDriverMessage = !this.switchSendEmail.removedByServer &&!PermissionsUtil.isUserDeniedLayout("lme/dispatch/DriverSendMessage");
        this.switchSendEmail.visible = canSendDriverMessage;
        if (canSendDriverMessage) {
            if (this.layoutSendLoad.panelZmitSwitches.visible) {
                this.layoutSendLoad.panelZmitSwitches.insert(this.switchSendEmail, this.layoutSendLoad.panelZmitSwitches.components.length-1);
            }
            this.layoutDriverSendMessage.setup(
                this.activeRow.get("driver_id", this.sourceEquipmentGroup.data[0].get("driver_id")),
                this.movementId,
                DriverMessageType.EMAIL,
                false
            );
            const sendDriverEmail = localStorage.getItem("send_driver_email");
            if (sendDriverEmail != null) {
                this.switchSendEmail.checked = JSON.parse(sendDriverEmail);
            }
        }
    }

    private createEmailWarningPanel(): Panel {
        const message = new Label({
            text: "A valid email address (or list of addresses) is required to send the email.",
            align: HorizontalAlignment.CENTER
        });
        const question = new Label({
            fontBold: true, fontSize: "large",
            text: "Would you like to proceed sending the load without emailing the driver?"
        });
        return new Panel({
            width: 550,
            align: HorizontalAlignment.CENTER,
            components: [message, question]
        });
    }

    setShipperConsigneeValues() {
        const stopsData = this.sourceMovementStops.data;
        this.sourcePreassignments.activeRow.set({
            "shipper_name": stopsData[0]?.get("stop.location_name"),
            "consignee_name": stopsData[stopsData.length-1]?.get("stop.location_name")
        });
    }
}
